import {
    berekenAantalBussen,
    berekenBusFrequentie,
    berekenBussenVerdeling,
    berekenPercentageBeginStation,
    PeriodeFactor,
    Reisperiode,
    Rijtijd,
    TvvStandaardRoute,
} from "@/entities/tvv";
import { useCallback } from "react";
import type { UseFormReturn } from "react-hook-form";
import type { BestellingenFormData } from "./schema";

type UseBestellingFormHandlersParams = {
    form: UseFormReturn<BestellingenFormData>;
    index: number;
    route: TvvStandaardRoute;
};

export function useBestellingFormHandlers({ form, index, route }: UseBestellingFormHandlersParams) {
    const rijtijd = Rijtijd.getTotaleRijtijd(route.rijtijden) ?? -1;
    const path = `bestellingen.${index}.bestelling` as const;

    const handleSliderChange = useCallback(
        (value: number[]) => {
            const aantalBussen = form.getValues(`${path}.aantalBussen`);
            const bussenBeginStation = value[0];
            form.setValue(`${path}.bussenBeginStation`, bussenBeginStation);
            form.setValue(`${path}.bussenEindStation`, aantalBussen - bussenBeginStation);
            form.setValue(`${path}.percentageBeginStation`, berekenPercentageBeginStation(bussenBeginStation, aantalBussen));
            form.setValue(`${path}.isAangepast`, true);
        },
        [form, path]
    );

    const handleFrequentieChange = useCallback(
        (newFrequentie: number) => {
            // busFrequentie is already set by field.onChange, compute derived values.
            // Zonder bekende rijtijd is frequentie <-> aantalBussen niet te berekenen; laat de velden los.
            form.setValue(`${path}.isAangepast`, true);
            if (rijtijd <= 0) return;
            const newAantalBussen = berekenAantalBussen(newFrequentie, rijtijd);
            const percentage = form.getValues(`${path}.percentageBeginStation`);
            const { bussenBeginStation } = berekenBussenVerdeling(newAantalBussen, percentage);
            form.setValue(`${path}.aantalBussen`, newAantalBussen);
            form.setValue(`${path}.bussenBeginStation`, bussenBeginStation);
            form.setValue(`${path}.bussenEindStation`, newAantalBussen - bussenBeginStation);
            form.setValue(`${path}.percentageBeginStation`, berekenPercentageBeginStation(bussenBeginStation, newAantalBussen));
        },
        [form, path, rijtijd]
    );

    const handleAantalBussenChange = useCallback(
        (newAantal: number) => {
            // aantalBussen is already set by field.onChange, compute derived values.
            // Zonder bekende rijtijd is aantalBussen <-> frequentie niet te berekenen; laat de velden los.
            form.setValue(`${path}.isAangepast`, true);
            if (rijtijd <= 0) return;
            const percentage = form.getValues(`${path}.percentageBeginStation`);
            const { bussenBeginStation } = berekenBussenVerdeling(newAantal, percentage);
            form.setValue(`${path}.busFrequentie`, berekenBusFrequentie(newAantal, rijtijd));
            form.setValue(`${path}.bussenBeginStation`, bussenBeginStation);
            form.setValue(`${path}.bussenEindStation`, newAantal - bussenBeginStation);
            form.setValue(`${path}.percentageBeginStation`, berekenPercentageBeginStation(bussenBeginStation, newAantal));
        },
        [form, path, rijtijd]
    );

    const handleBeginStationChange = useCallback(
        (newBegin: number) => {
            const aantalBussen = form.getValues(`${path}.aantalBussen`);
            form.setValue(`${path}.bussenBeginStation`, newBegin);
            form.setValue(`${path}.bussenEindStation`, aantalBussen - newBegin);
            form.setValue(`${path}.percentageBeginStation`, berekenPercentageBeginStation(newBegin, aantalBussen));
            form.setValue(`${path}.isAangepast`, true);
        },
        [form, path]
    );

    const handleEindStationChange = useCallback(
        (newEind: number) => {
            const aantalBussen = form.getValues(`${path}.aantalBussen`);
            const bussenBeginStation = aantalBussen - newEind;
            form.setValue(`${path}.bussenBeginStation`, bussenBeginStation);
            form.setValue(`${path}.bussenEindStation`, newEind);
            form.setValue(`${path}.percentageBeginStation`, berekenPercentageBeginStation(bussenBeginStation, aantalBussen));
            form.setValue(`${path}.isAangepast`, true);
        },
        [form, path]
    );

    const handleReisperiodeChange = useCallback(
        (value: string) => {
            // reisperiode is already set by field.onChange, reset to initial values for new period
            const newReisperiode = value as Reisperiode;
            const periodeFactor = TvvStandaardRoute.getPeriodeFactor(route, newReisperiode);

            if (periodeFactor) {
                const initWaarden = PeriodeFactor.getInitieleBestelWaarden(periodeFactor, rijtijd);
                form.setValue(`${path}.busFrequentie`, initWaarden.busFrequentie);
                form.setValue(`${path}.aantalBussen`, initWaarden.aantalBussen);
                form.setValue(`${path}.bussenBeginStation`, initWaarden.bussenBeginStation);
                form.setValue(`${path}.bussenEindStation`, initWaarden.bussenEindStation);
                form.setValue(`${path}.percentageBeginStation`, initWaarden.percentageBeginStation);
                form.setValue(`${path}.isAangepast`, false);
            }
        },
        [form, path, route, rijtijd]
    );

    return {
        path,
        handleSliderChange,
        handleFrequentieChange,
        handleAantalBussenChange,
        handleBeginStationChange,
        handleEindStationChange,
        handleReisperiodeChange,
    };
}
